import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API } from "../../config/api";
import "./../Styles/workdetails.css";
import WorkDescription from "./WorkDescription";
export default function WorkDetails() {
  const location = useLocation();
  const navigate = useNavigate();

  const work = location.state?.work;

  const BASE_URL = `${API.BASE_URL}/api.php`;
  const UPLOAD_URL = `${API.BASE_URL}/uploads/work`;

  const user = JSON.parse(sessionStorage.getItem("user"));

  const [selectedImage, setSelectedImage] = useState(0);

  // =========================
  // BID STATES
  // =========================
  const [commission, setCommission] = useState(0);
  const [showBidModal, setShowBidModal] = useState(false);
  const [priceInput, setPriceInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // =========================
  // IMAGE POPUP
  // =========================
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [popupImageIndex, setPopupImageIndex] = useState(0);

  // =========================
  // IMAGE HOVER ZOOM
  // =========================
  const [showZoom, setShowZoom] = useState(false);

  const [zoomPosition, setZoomPosition] = useState({
    x: 50,
    y: 50,
  });

  // =========================
  // IMAGES
  // =========================
  const images =
    Array.isArray(work?.images) && work.images.length > 0 ? work.images : [];

  const getImageUrl = (image) => `${UPLOAD_URL}/${work?.owner_id}/${image}`;

  const DESIGN_TYPES = ["Multi", "Sequence", "Cording", "Beads"];

  // =========================
  // DESCRIPTION DATA
  // =========================
  const getDescriptionData = (description = "") => {
    const selectedTypes = [];
    let remainingDescription = description;

    DESIGN_TYPES.forEach((type) => {
      const regex = new RegExp(`(^|,|\\n)\\s*${type}\\s*(?=,|\\n|$)`, "gi");

      // Check if this design type exists
      if (regex.test(description)) {
        selectedTypes.push(type);
      }

      // Fresh regex because global regex changes lastIndex
      const removeRegex = new RegExp(
        `(^|,|\\n)\\s*${type}\\s*(?=,|\\n|$)`,
        "gi",
      );

      remainingDescription = remainingDescription.replace(removeRegex, "$1");
    });

    // Clean remaining description
    remainingDescription = remainingDescription
      .replace(/,\s*,/g, ",")
      .replace(/^\s*,\s*/g, "")
      .replace(/\s*,\s*$/g, "")
      .replace(/^\s*\n/gm, "")
      .replace(/\n{2,}/g, "\n")
      .trim();

    return {
      designTypes: selectedTypes,
      customDescription: remainingDescription,
    };
  };

  // =========================
  // IMAGE KEYBOARD NAVIGATION
  // =========================
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't change images when typing in input/textarea/select
      const tagName = e.target.tagName?.toLowerCase();

      if (
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select"
      ) {
        return;
      }

      // Escape closes popup
      if (e.key === "Escape" && showImagePopup) {
        setShowImagePopup(false);
        return;
      }

      if (images.length <= 1) return;

      if (e.key === "ArrowLeft") {
        e.preventDefault();

        if (showImagePopup) {
          setPopupImageIndex((prev) =>
            prev === 0 ? images.length - 1 : prev - 1,
          );
        } else {
          setSelectedImage((prev) =>
            prev === 0 ? images.length - 1 : prev - 1,
          );
        }
      }

      if (e.key === "ArrowRight") {
        e.preventDefault();

        if (showImagePopup) {
          setPopupImageIndex((prev) =>
            prev === images.length - 1 ? 0 : prev + 1,
          );
        } else {
          setSelectedImage((prev) =>
            prev === images.length - 1 ? 0 : prev + 1,
          );
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showImagePopup, images.length]);

  // =========================
  // GET COMMISSION
  // =========================
  useEffect(() => {
    const getCommission = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}?action=get_platform_bid_commission`,
        );

        const result = await response.json();

        if (result.status) {
          setCommission(Number(result.commission));
        }
      } catch (error) {
        console.error("Commission error:", error);
      }
    };

    getCommission();
  }, [BASE_URL]);

  // =========================
  // IF WORK NOT FOUND
  // =========================
  if (!work) {
    return (
      <div className="wd-empty">
        <i className="bi bi-exclamation-circle"></i>

        <h3>Work Not Found</h3>

        <p>This work details page was opened without selecting a work.</p>

        <button type="button" onClick={() => navigate("/Work-list")}>
          <i className="bi bi-arrow-left"></i>
          Back to Works
        </button>
      </div>
    );
  }

  // =========================
  // MAIN IMAGE NAVIGATION
  // =========================
  const nextImage = () => {
    if (images.length === 0) return;

    setSelectedImage((prev) => (prev === images.length - 1 ? 0 : prev + 1));

    // Keep zoom position centered after image change
    setZoomPosition({
      x: 50,
      y: 50,
    });
  };

  const prevImage = () => {
    if (images.length === 0) return;

    setSelectedImage((prev) => (prev === 0 ? images.length - 1 : prev - 1));

    // Keep zoom position centered after image change
    setZoomPosition({
      x: 50,
      y: 50,
    });
  };

  // =========================
  // OPEN IMAGE POPUP
  // =========================
  const openImagePopup = (index) => {
    setPopupImageIndex(index);
    setShowImagePopup(true);
  };

  const nextPopupImage = () => {
    if (images.length === 0) return;

    setPopupImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevPopupImage = () => {
    if (images.length === 0) return;

    setPopupImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  // =========================
  // IMAGE HOVER ZOOM
  // =========================
  const handleImageMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();

    const x = ((e.clientX - rect.left) / rect.width) * 100;

    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setZoomPosition({
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y)),
    });
  };

  const handleImageMouseEnter = () => {
    setShowZoom(true);
  };

  const handleImageMouseLeave = () => {
    setShowZoom(false);
  };

  // =========================
  // OPEN ADD BID
  // =========================
  const handleOpenAddBid = () => {
    if (!user) {
      alert("Please login first.");
      return;
    }

    const providerOptions = user.provider_options || "";

    const isDesigner = Array.isArray(providerOptions)
      ? providerOptions.some(
          (item) => String(item).toLowerCase() === "designers",
        )
      : String(providerOptions)
          .toLowerCase()
          .split(",")
          .map((v) => v.trim())
          .includes("designers");

    if (!isDesigner) {
      alert(
        "Only Designer accounts can add bids.\n\nPlease update your profile and enable the Designer option.",
      );
      return;
    }

    setPriceInput("");
    setShowBidModal(true);
  };

  // =========================
  // CONFIRM BID
  // =========================
  const handleConfirmAddBid = async (e) => {
    e.preventDefault();

    if (!priceInput.trim() || isNaN(priceInput) || Number(priceInput) <= 0) {
      alert("Please enter a valid positive price.");
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();

    formData.append("user_id", user.id);
    formData.append("work_id", work.id);
    formData.append("price", priceInput);

    try {
      const response = await fetch(`${BASE_URL}?action=add_beads_from_work`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      alert(result.message);

      if (result.status) {
        setShowBidModal(false);
        setPriceInput("");

        const userTab = window.open("", "EmbroideryUser");

        if (userTab) {
          userTab.location.href = `${window.location.origin}/user/myBids`;
          userTab.focus();
        }
      }
    } catch (error) {
      console.error("Add bid error:", error);

      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // =========================
  // BID CALCULATION
  // =========================
  const bidPrice = Number(priceInput || 0);

  const commissionAmount = (bidPrice * commission) / 100;

  const finalAmount = bidPrice - commissionAmount;

  const { designTypes, customDescription } = getDescriptionData(
    work.description || "",
  );

  // =========================
  // RETURN
  // =========================
  return (
    <>
      <div className="wd-page">
        {/* ================= HEADER ================= */}

        <div className="wd-header">
          <button
            type="button"
            className="wd-back-btn"
            onClick={() => navigate(-1)}
          >
            <i className="bi bi-arrow-left"></i>
            Back
          </button>

          <div className="wd-title">
            <h2>Work Details</h2>
          </div>
        </div>

        {/* ================= MAIN ================= */}

        <div className="wd-container">
          {/* ================= LEFT IMAGES ================= */}

          <div className="wd-left">
            <div className="wd-main-image">
              {images.length > 0 ? (
                <>
                  {/* ================= IMAGE HOVER AREA ================= */}

                  <div
                    className="wd-image-hover-area"
                    onMouseEnter={handleImageMouseEnter}
                    onMouseMove={handleImageMouseMove}
                    onMouseLeave={handleImageMouseLeave}
                  >
                    {/* MAIN IMAGE */}

                    <img
                      src={getImageUrl(images[selectedImage])}
                      alt={`Work ${work.id}`}
                      className="wd-clickable-image"
                      onClick={() => openImagePopup(selectedImage)}
                      title="Click to view image"
                    />

                    {/* ================= ZOOM BUTTON ================= */}

                    <button
                      type="button"
                      className="wd-image-zoom"
                      onClick={() => openImagePopup(selectedImage)}
                      title="View Image"
                    >
                      <i className="bi bi-arrows-angle-expand"></i>
                    </button>
                    {showZoom && (
                      <div
                        className="zoom-hover-indicator"
                        style={{
                          left: `${zoomPosition.x}%`,
                          top: `${zoomPosition.y}%`,
                        }}
                      />
                    )}
                    {/* ================= IMAGE ARROWS ================= */}

                    {images.length > 1 && (
                      <>
                        <button
                          type="button"
                          className="wd-image-prev"
                          onClick={prevImage}
                        >
                          <i className="bi bi-chevron-left"></i>
                        </button>

                        <button
                          type="button"
                          className="wd-image-next"
                          onClick={nextImage}
                        >
                          <i className="bi bi-chevron-right"></i>
                        </button>

                        <div className="wd-image-counter">
                          {selectedImage + 1} / {images.length}
                        </div>
                      </>
                    )}

                    {/* ================= ZOOM BOX ================= */}

                    {showZoom && (
                      <div className="wd-zoom-box">
                        <img
                          src={getImageUrl(images[selectedImage])}
                          alt="Zoomed work"
                          className="wd-zoom-image"
                          style={{
                            transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                          }}
                        />
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="wd-no-image">
                  <i className="bi bi-image"></i>

                  <span>No Image Available</span>
                </div>
              )}
            </div>

            {/* ================= THUMBNAILS ================= */}

            {images.length > 1 && (
              <div className="wd-thumbnails">
                {images.map((image, index) => (
                  <button
                    type="button"
                    key={index}
                    className={`wd-thumbnail ${
                      selectedImage === index ? "wd-thumbnail-active" : ""
                    }`}
                    onClick={() => {
                      setSelectedImage(index);
                      setZoomPosition({
                        x: 50,
                        y: 50,
                      });
                    }}
                  >
                    <img
                      src={getImageUrl(image)}
                      alt={`Thumbnail ${index + 1}`}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ================= RIGHT INFORMATION ================= */}

          <div className="wd-right">
            <div className="wd-info-header">
              <div>
                <span className="wd-label">WORK DETAILS</span>
              </div>
            </div>

            {/* DATE */}

            <div className="wd-date">
              <i className="bi bi-calendar-event"></i>

              <span>
                {work.created_at
                  ? new Date(work.created_at).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "-"}
              </span>
            </div>

            {/* SPECS */}

            <div className="wd-specs">
              <div className="wd-spec">
                <span>Height</span>

                <strong>{work.height || "-"} mm</strong>
              </div>

              <div className="wd-spec">
                <span>Area</span>

                <strong>{work.area || "-"} mm</strong>
              </div>

              <div className="wd-spec">
                <span>Color</span>

                <strong>{work.color || "-"}</strong>
              </div>
            </div>

            {/* DESCRIPTION */}

            <div className="wd-section">
              <h3>
                <i className="bi bi-file-text"></i>
                Description
              </h3>

              {/* SELECTED DESIGN TYPES */}

              {designTypes.length > 0 && (
                <div className="wd-design-types">
                  {designTypes.map((type) => (
                    <span
                      key={type}
                      className={`wd-design-type wd-design-type-${type
                        .toLowerCase()
                        .replace(/\s/g, "")}`}
                    >
                      <i className="bi bi-check-circle-fill"></i>
                      {type}
                    </span>
                  ))}
                </div>
              )}

              {/* CUSTOM DESCRIPTION */}

              {customDescription && (
                <div className="wd-description">{customDescription}</div>
              )}
            </div>

            {/* ================= ADD BID ================= */}

            <div className="wd-bid-action">
              <button
                type="button"
                className="wd-add-bid-btn"
                onClick={handleOpenAddBid}
              >
                <i className="bi bi-plus-circle-fill"></i>
                Add Bids
              </button>
            </div>
          </div>
        </div>
        <WorkDescription work={work} designTypes={designTypes} />
      </div>

      {/* =====================================================
          IMAGE POPUP
      ===================================================== */}

      {showImagePopup && images.length > 0 && (
        <div
          className="wd-image-popup-overlay"
          onClick={() => setShowImagePopup(false)}
        >
          <div className="wd-image-popup" onClick={(e) => e.stopPropagation()}>
            {/* CLOSE */}

            <button
              type="button"
              className="wd-popup-close"
              onClick={() => setShowImagePopup(false)}
            >
              <i className="bi bi-x-lg"></i>
            </button>

            {/* IMAGE */}

            <img
              src={getImageUrl(images[popupImageIndex])}
              alt={`Work ${work.id} preview`}
              className="wd-popup-image"
            />

            {/* PREVIOUS */}

            {images.length > 1 && (
              <button
                type="button"
                className="wd-popup-prev"
                onClick={prevPopupImage}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
            )}

            {/* NEXT */}

            {images.length > 1 && (
              <button
                type="button"
                className="wd-popup-next"
                onClick={nextPopupImage}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            )}

            {/* COUNTER */}

            {images.length > 1 && (
              <div className="wd-popup-counter">
                {popupImageIndex + 1} / {images.length}
              </div>
            )}
          </div>
        </div>
      )}

      {/* =====================================================
          ADD BID POPUP
      ===================================================== */}

      {showBidModal && (
        <div
          className="eb-modal-backdrop"
          onClick={() => {
            if (!isSubmitting) {
              setShowBidModal(false);
            }
          }}
        >
          <div className="eb-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="eb-modal-header">
              <h5>Add Bids Option</h5>

              <button
                type="button"
                className="eb-modal-close-btn"
                onClick={() => setShowBidModal(false)}
                disabled={isSubmitting}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleConfirmAddBid}>
              <div className="eb-modal-body">
                <p className="eb-modal-desc">
                  Specify your quote for this work request.
                </p>

                <div className="eb-input-group">
                  <label>Bid Price (₹)</label>

                  <input
                    type="number"
                    min="1"
                    step="any"
                    placeholder="Enter your bid"
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                {/* ================= BID CALCULATION ================= */}

                <div
                  style={{
                    marginTop: 20,
                    background: "#f8f9fa",
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    padding: 15,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <span>Your Bid</span>

                    <strong>₹ {bidPrice.toFixed(2)}</strong>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 8,
                      color: "#dc3545",
                    }}
                  >
                    <span>Platform Commission ({commission}%)</span>

                    <strong>- ₹ {commissionAmount.toFixed(2)}</strong>
                  </div>

                  <hr />

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 18,
                    }}
                  >
                    <span>You Will Receive</span>

                    <strong
                      style={{
                        color: "#198754",
                      }}
                    >
                      ₹ {finalAmount.toFixed(2)}
                    </strong>
                  </div>
                </div>
              </div>

              {/* ================= FOOTER ================= */}

              <div className="eb-modal-footer">
                <button
                  type="button"
                  className="eb-btn-secondary"
                  onClick={() => setShowBidModal(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="eb-btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Confirm & Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
